import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { recalcAll, getDefaultHsn, getEffectiveGstRate } from '@/lib/gstHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnterBill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [nextBillNo, setNextBillNo] = useState('');
  const [districts, setDistricts] = useState([]);
  const [isCopiedData, setIsCopiedData] = useState(false);
  
  // Helper function to get current month name
  const getCurrentMonth = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[new Date().getMonth()];
  };

  // Helper function to get current year
  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };
  
  const [formData, setFormData] = useState({
    fy: '2026-27',
    document_type: 'Bill',
    magazine: '',
    type: '',
    product: '',
    po_wo_no: '',
    proforma_invoice_no: '',
    entity_id: '',
    initial: 'M/s.',
    billing_name: '',
    bill_receipt_name: '',
    billing_address: '',
    billing_state: '',
    district: '',
    pincode: '',
    gst_no: '',
    occasion: '',
    bill_date: new Date().toISOString().split('T')[0],
    start_month: '',
    start_year: '',
    end_month: '',
    end_year: '',
    advertisement_position: '',
    advertisement_tenure: '',
    item_description: '',
    include_in_aabkari: false,
    gross_amount: '',
    discount_percentage: '',
    discount_amount: '',
    sgst_amount: 0,
    cgst_amount: 0,
    igst_amount: 0,
    total_amount: '',
    gst_rate: 'auto',
    hsn_sac: '',
    last_edited_field: '',
    bill_no: '',
    terms_conditions: '1. Artwork approval required before printing for multiple issues.\n2. Post-order confirmation, PO with full terms should be provided.\n3. Dimensions: Full Page Size of 7.25" x 10.5" (A4)\n4. Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)\n5. 1 print copy shall be sent at your registered postal address.'
  });
  
  const [canCreateBill, setCanCreateBill] = useState(true);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  // Once user manually types in Item Description, stop the auto-effect from overwriting.
  // Lazy initializer: when navigating from Copy, mark as edited on the very
  // first render so the auto-fill effect never overwrites the copied text.
  const [userEditedDescription, setUserEditedDescription] = useState(
    () => !!location.state?.copyFrom?.item_description
  );

  const [calculatedFields, setCalculatedFields] = useState({
    sgst_amount: 0,
    cgst_amount: 0,
    igst_amount: 0,
    total_amount: 0,
    amount_in_words: ''
  });

  // Legacy state retained for compat (no longer used after gstHelper migration).
  // eslint-disable-next-line no-unused-vars
  const [totalAmountInput, setTotalAmountInput] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [isCalculatingFromTotal, setIsCalculatingFromTotal] = useState(false);

  useEffect(() => {
    if (formData.magazine && formData.document_type && formData.fy) {
      fetchNextBillNo();
    }
  }, [formData.magazine, formData.document_type, formData.fy]);

  // Sync bill_no with nextBillNo when nextBillNo changes
  useEffect(() => {
    if (nextBillNo && !formData.bill_no) {
      setFormData(prev => ({ ...prev, bill_no: nextBillNo }));
    }
  }, [nextBillNo]);

  // Pre-fill form when navigating from Copy button
  useEffect(() => {
    if (location.state?.copyFrom) {
      const copied = location.state.copyFrom;
      setIsCopiedData(true);
      // Mark description as user-edited so the auto-fill effect does not overwrite
      // the copied item_description.
      if (copied.item_description) {
        setUserEditedDescription(true);
      }
      setFormData(prev => ({
        ...prev,
        ...copied,
        bill_no: '',
        bill_date: new Date().toISOString().split('T')[0],
      }));
      toast.info('Data copied. A new Bill number will be assigned automatically.');
    }
  }, []);

  // Fetch districts when billing state changes
  const fetchDistricts = async (state) => {
    if (!state) {
      setDistricts([]);
      return;
    }
    try {
      // Try members first, then advertisers
      let response;
      try {
        response = await axios.get(`${API}/members/unique-districts/${encodeURIComponent(state)}`);
      } catch {
        response = await axios.get(`${API}/advertisers/unique-districts/${encodeURIComponent(state)}`);
      }
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  // Auto-fill from advertiser/member when entity_id changes
  useEffect(() => {
    if (formData.type === 'Advertiser' && formData.entity_id) {
      fetchAdvertiserDetails(formData.entity_id);
    } else if (formData.type === 'Member' && formData.entity_id) {
      fetchMemberDetails(formData.entity_id);
    } else if (formData.type === 'Advertiser' && !formData.entity_id) {
      // Disable billing if Type is Advertiser but no ID entered
      setCanCreateBill(false);
    } else {
      setCanCreateBill(true);
    }
  }, [formData.entity_id, formData.type]);

  const fetchAdvertiserDetails = async (advertiserId) => {
    try {
      // Try direct API first (for exact ID)
      let advertiser = null;
      try {
        const response = await axios.get(`${API}/advertisers/${advertiserId}`);
        advertiser = response.data;
      } catch (directError) {
        // If direct fetch fails, search with partial/case-insensitive match
        const searchResponse = await axios.post(`${API}/advertisers/search`, { advertiser_id: advertiserId });
        if (searchResponse.data && searchResponse.data.length > 0) {
          // Find exact or partial match (case-insensitive)
          const searchTerm = advertiserId.toUpperCase();
          advertiser = searchResponse.data.find(adv => 
            adv.advertiser_id.toUpperCase() === searchTerm || 
            adv.advertiser_id.toUpperCase().includes(searchTerm) ||
            adv.advertiser_id.match(new RegExp(searchTerm.replace(/[^0-9]/g, ''), 'i'))
          ) || searchResponse.data[0];
        }
      }
      
      if (!advertiser) {
        throw new Error('Advertiser not found');
      }
      
      // Clean pincode - remove .0 if present and ensure 6 digits
      let cleanPincode = '';
      if (advertiser.pincode) {
        cleanPincode = String(advertiser.pincode).split('.')[0]; // Remove .0
        cleanPincode = cleanPincode.padStart(6, '0'); // Ensure 6 digits
      }
      
      // Auto-fill fields
      setFormData(prev => ({
        ...prev,
        initial: advertiser.initials || '',
        billing_name: advertiser.company_name || '',
        bill_receipt_name: advertiser.company_name || '',
        billing_address: advertiser.address || '',
        billing_state: advertiser.state || '',
        district: advertiser.district || '',
        pincode: cleanPincode,
        gst_no: advertiser.gst_no || '',
        po_wo_no: advertiser.po_wo_no || ''
      }));
      setCanCreateBill(true);
    } catch (error) {
      console.error('Error fetching advertiser:', error);
      toast.error('Advertiser not found. Please register advertiser first.');
      setCanCreateBill(false);
    }
  };

  const fetchMemberDetails = async (memberId) => {
    try {
      // Try direct API first (for exact ID)
      let member = null;
      try {
        const response = await axios.get(`${API}/members/${memberId}`);
        member = response.data;
      } catch (directError) {
        // If direct fetch fails, search with partial/case-insensitive match
        const searchResponse = await axios.post(`${API}/members/search`, { member_id: memberId });
        if (searchResponse.data && searchResponse.data.length > 0) {
          // Find exact or partial match (case-insensitive)
          const searchTerm = memberId.toUpperCase();
          member = searchResponse.data.find(mem => 
            mem.member_id.toUpperCase() === searchTerm || 
            mem.member_id.toUpperCase().includes(searchTerm) ||
            mem.member_id.match(new RegExp(searchTerm.replace(/[^0-9]/g, ''), 'i'))
          ) || searchResponse.data[0];
        }
      }
      
      if (!member) {
        throw new Error('Member not found');
      }
      
      // Clean pincode - remove .0 if present and ensure 6 digits
      let cleanPincode = '';
      if (member.pincode) {
        cleanPincode = String(member.pincode).split('.')[0];
        cleanPincode = cleanPincode.padStart(6, '0');
      }
      
      // Auto-fill fields
      setFormData(prev => ({
        ...prev,
        initial: member.initials || '',
        billing_name: member.company_name || member.name || '',
        bill_receipt_name: member.company_name || member.name || '',
        billing_address: member.address || '',
        billing_state: member.state || '',
        district: member.district || '',
        pincode: cleanPincode,
        gst_no: '' // Members typically don't have GST
      }));
      setCanCreateBill(true);
    } catch (error) {
      console.error('Error fetching member:', error);
      toast.error('Member not found. Please register member first.');
      setCanCreateBill(false);
    }
  };

  // Calculate GST and total using gstHelper.
  // Direction depends on which field the user last edited (gross/discount → forward,
  // total → reverse). When 'total' is the last-edited field, gross_amount is the one
  // that gets re-derived.
  useEffect(() => {
    const next = recalcAll({
      ...formData,
      gross_amount: parseFloat(formData.gross_amount) || 0,
      discount_amount: parseFloat(formData.discount_amount) || 0,
      total_amount: parseFloat(formData.total_amount) || 0,
    });
    setCalculatedFields({
      sgst_amount: next.sgst_amount || 0,
      cgst_amount: next.cgst_amount || 0,
      igst_amount: next.igst_amount || 0,
      total_amount: next.total_amount || 0,
      amount_in_words: numberToWords(next.total_amount || 0),
    });
  }, [
    formData.gross_amount,
    formData.discount_amount,
    formData.total_amount,
    formData.billing_state,
    formData.magazine,
    formData.product,
    formData.gst_rate,
    formData.last_edited_field,
  ]);

  // Update Terms & Conditions based on product type and magazine
  useEffect(() => {
    if (isCopiedData) return; // Don't overwrite terms when data was copied
    
    const advertiserTC = '1. Artwork approval required before printing for multiple issues.\n2. Post-order confirmation, PO with full terms should be provided.\n3. Dimensions: Full Page Size of 7.25" x 10.5" (A4)\n4. Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)\n5. 1 print copy shall be sent at your registered postal address.';
    
    const aabkariMemberTC = 'Terms & Conditions:\n1. All disputes are subject to the jurisdiction of Allahabad (Prayagraj) Courts only.\n2. Payment shall be made through Account Payee Cheque / DD / RTGS / NEFT in favour of "Aabkari Times", payable at Prayagraj.\n3. GST not applicable on subscription charges. GST shall be levied on advertisement services as applicable.';
    
    const sugarMemberTC = 'Terms & Conditions:\n1. All disputes are subject to the jurisdiction of Allahabad (Prayagraj) Courts only.\n2. Payment shall be made through Account Payee Cheque / DD / RTGS / NEFT in favour of "Sugar Times", payable at Prayagraj.';
    
    // Determine appropriate T&C based on product and magazine
    if (formData.product === 'Subscription') {
      if (formData.magazine === 'Aabkari Times') {
        setFormData(prev => ({ ...prev, terms_conditions: aabkariMemberTC }));
      } else if (formData.magazine === 'Sugar Times') {
        setFormData(prev => ({ ...prev, terms_conditions: sugarMemberTC }));
      }
    } else if (formData.product === 'Advertisement' || formData.product === 'Subscription/Advertisement') {
      setFormData(prev => ({ ...prev, terms_conditions: advertiserTC }));
    }
  }, [formData.product, formData.magazine]);

  // Auto-populate item description with default text (stop once user manually edits)
  useEffect(() => {
    if (userEditedDescription) return;
    const defaultDesc = `Towards your ${formData.product || 'Subscription'} charges of ${formData.magazine ? formData.magazine + ' magazine' : 'Magazine'} for ${formData.advertisement_tenure || '1 Month'}`;
    setFormData(prev => ({ ...prev, item_description: defaultDesc }));
  }, [formData.product, formData.magazine, formData.advertisement_tenure, userEditedDescription]);

  // Auto-populate HSN/SAC based on product (only when blank / default)
  useEffect(() => {
    const def = getDefaultHsn(formData.product);
    if (!formData.hsn_sac || formData.hsn_sac === '998363' || formData.hsn_sac === '998397') {
      setFormData(prev => ({ ...prev, hsn_sac: def }));
    }
  }, [formData.product]);

  // (Reverse-math is now handled by the gstHelper recalcAll effect via last_edited_field='total'.)

  // Helper function for number to words
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero Rupees Only';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    const roundedNum = Math.round(num);
    
    if (roundedNum < 1000) {
      return 'Rupees ' + convertLessThanThousand(roundedNum) + ' Only';
    }
    
    if (roundedNum < 100000) {
      const thousands = Math.floor(roundedNum / 1000);
      const remainder = roundedNum % 1000;
      return 'Rupees ' + convertLessThanThousand(thousands) + ' Thousand' + 
             (remainder !== 0 ? ' ' + convertLessThanThousand(remainder) : '') + ' Only';
    }
    
    if (roundedNum < 10000000) {
      const lakhs = Math.floor(roundedNum / 100000);
      const remainder = roundedNum % 100000;
      const thousands = Math.floor(remainder / 1000);
      const hundreds = remainder % 1000;
      
      let result = 'Rupees ' + convertLessThanThousand(lakhs) + ' Lakh';
      if (thousands !== 0) result += ' ' + convertLessThanThousand(thousands) + ' Thousand';
      if (hundreds !== 0) result += ' ' + convertLessThanThousand(hundreds);
      return result + ' Only';
    }
    
    const crores = Math.floor(roundedNum / 10000000);
    const remainder = roundedNum % 10000000;
    const lakhs = Math.floor(remainder / 100000);
    const thousands = Math.floor((remainder % 100000) / 1000);
    const hundreds = remainder % 1000;
    
    let result = 'Rupees ' + convertLessThanThousand(crores) + ' Crore';
    if (lakhs !== 0) result += ' ' + convertLessThanThousand(lakhs) + ' Lakh';
    if (thousands !== 0) result += ' ' + convertLessThanThousand(thousands) + ' Thousand';
    if (hundreds !== 0) result += ' ' + convertLessThanThousand(hundreds);
    return result + ' Only';
  };

  const fetchNextBillNo = async () => {
    if (!formData.magazine || !formData.fy) {
      setNextBillNo('Select magazine and FY first');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/bills/next-no`, {
        params: {
          magazine: formData.magazine,
          document_type: formData.document_type,
          fy: formData.fy
        }
      });
      setNextBillNo(response.data.next_no);
    } catch (error) {
      console.error('Error fetching next bill number:', error);
      toast.error('Failed to fetch next bill number');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for Advertiser type
    if (formData.type === 'Advertiser' && !formData.entity_id) {
      toast.error('Please enter Advertiser ID or register advertiser before creating bill');
      return;
    }
    
    if (!canCreateBill) {
      toast.error('Please register advertiser before creating bill');
      return;
    }
    
    // Detailed validation with specific field names
    const missingFields = [];
    if (!formData.magazine) missingFields.push('Magazine');
    if (!formData.initial) missingFields.push('Initial');
    if (!formData.billing_name) missingFields.push('Billing Name');
    if (!formData.billing_state) missingFields.push('Billing State');
    if (!formData.gross_amount || formData.gross_amount <= 0) missingFields.push('Gross Amount');
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Final bidirectional recalc — pulls reverse-derived gross when user typed Total
      const final = recalcAll({
        ...formData,
        gross_amount: parseFloat(formData.gross_amount) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        total_amount: parseFloat(formData.total_amount) || 0,
      });
      const submitData = {
        ...formData,
        gross_amount: final.gross_amount,
        discount_percentage: final.discount_percentage ?? (parseFloat(formData.discount_percentage) || 0),
        discount_amount: final.discount_amount,
        sgst_amount: final.sgst_amount,
        cgst_amount: final.cgst_amount,
        igst_amount: final.igst_amount,
        total_amount: final.total_amount,
        gst_rate: formData.gst_rate || 'auto',
        hsn_sac: formData.hsn_sac || '',
        amount_in_words: numberToWords(final.total_amount),
      };
      await axios.post(`${API}/bills`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(`Bill created successfully with Bill No: ${nextBillNo}`);
      
      // Reset form
      setFormData({
        fy: '2026-27',
        document_type: 'Bill',
        magazine: '',
        type: '',
        product: '',
        po_wo_no: '',
        proforma_invoice_no: '',
        entity_id: '',
        initial: 'M/s.',
        billing_name: '',
        bill_receipt_name: '',
        billing_address: '',
        billing_state: '',
        district: '',
        pincode: '',
        gst_no: '',
        occasion: '',
        bill_date: new Date().toISOString().split('T')[0],
        start_month: '',
        start_year: '',
        end_month: '',
        end_year: '',
        advertisement_position: '',
        advertisement_tenure: '',
        gross_amount: '',
        bill_no: '',
        terms_conditions: '1. Artwork approval required before printing for multiple issues.\n2. Post-order confirmation, PO with full terms should be provided.\n3. Dimensions: Full Page Size of 7.25" x 10.5" (A4)\n4. Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)\n5. 1 print copy shall be sent at your registered postal address.'
      });
      setNextBillNo('');
      setCalculatedFields({
        sgst_amount: 0,
        cgst_amount: 0,
        igst_amount: 0,
        total_amount: 0,
        amount_in_words: ''
      });
    } catch (error) {
      console.error('Error creating bill:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create bill: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() + i - 2).toString());

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }} data-testid="enter-bill-title">
              Enter Bill or Receipt
            </h1>
          </div>
          {nextBillNo && (
            <div className="text-xl font-semibold" style={{ color: '#7c3aed' }}>
              Next Bill No: {nextBillNo}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="form-section">
          {/* Document Type */}
          <div className="mb-6">
            <Label className="mb-3 block">Document Type</Label>
            <div className="flex space-x-6">
              <label className="flex items-center space-x-2">
                <input type="radio" name="document_type" value="Bill" checked={formData.document_type === 'Bill'} onChange={(e) => setFormData({ ...formData, document_type: e.target.value })} />
                <span>Bill</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" name="document_type" value="Receipt" checked={formData.document_type === 'Receipt'} onChange={(e) => setFormData({ ...formData, document_type: e.target.value })} />
                <span>Receipt</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">For Proforma Invoices, use the dedicated "Create Proforma Invoice" page</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Financial Year *</Label>
              <Select value={formData.fy} onValueChange={(value) => setFormData({ ...formData, fy: value })}>
                <SelectTrigger data-testid="fy-select">
                  <SelectValue placeholder="Select FY" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026-27">2026-27</SelectItem>
                  <SelectItem value="2025-26">2025-26</SelectItem>
                  <SelectItem value="2024-25">2024-25</SelectItem>
                  <SelectItem value="2023-24">2023-24</SelectItem>
                  <SelectItem value="2022-23">2022-23</SelectItem>
                  <SelectItem value="2021-22">2021-22</SelectItem>
                  <SelectItem value="2020-21">2020-21</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={formData.magazine} onValueChange={(value) => setFormData({ ...formData, magazine: value })}>
                <SelectTrigger data-testid="magazine-select">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger data-testid="type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Advertiser">Advertiser</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={formData.product} onValueChange={(value) => setFormData({ ...formData, product: value })}>
                <SelectTrigger data-testid="product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Subscription">Subscription</SelectItem>
                  <SelectItem value="Renewal">Renewal</SelectItem>
                  <SelectItem value="Advertisement">Advertisement</SelectItem>
                  <SelectItem value="Subscription/Advertisement">Subscription/Advertisement</SelectItem>
                  <SelectItem value="Event">Event</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Bill No (editable)</Label>
              <Input 
                value={formData.bill_no} 
                onChange={(e) => setFormData({ ...formData, bill_no: e.target.value })} 
                placeholder="eg. 107" 
              />
              {nextBillNo && formData.bill_no !== nextBillNo && (
                <p className="text-xs text-gray-500">Suggested: {nextBillNo}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Bill Date *</Label>
              <Input 
                type="date"
                value={formData.bill_date} 
                onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>P.O./W.O. No. (if any)</Label>
              <Input value={formData.po_wo_no} onChange={(e) => setFormData({ ...formData, po_wo_no: e.target.value })} placeholder="Enter P.O./W.O. No." />
            </div>

            <div className="space-y-2">
              <Label>Id</Label>
              <Input value={formData.entity_id} onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })} placeholder="eg. ATMR12" />
            </div>

            <div className="space-y-2">
              <Label>Initial *</Label>
              <Select value={formData.initial} onValueChange={(value) => setFormData({ ...formData, initial: value })}>
                <SelectTrigger data-testid="initial-select">
                  <SelectValue placeholder="Select initial" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mr.">Mr.</SelectItem>
                  <SelectItem value="Mrs.">Mrs.</SelectItem>
                  <SelectItem value="Ms.">Ms.</SelectItem>
                  <SelectItem value="Dr.">Dr.</SelectItem>
                  <SelectItem value="M/s.">M/s.</SelectItem>
                  <SelectItem value="The">The</SelectItem>
                  <SelectItem value="To">To</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Billing Name *</Label>
              <Input 
                value={formData.billing_name} 
                onChange={(e) => setFormData({ ...formData, billing_name: e.target.value })} 
                placeholder={formData.type === 'Advertiser' ? 'Auto-filled from advertiser (editable)' : 'Enter billing name'} 
                required
              />
              {formData.type === 'Advertiser' && !formData.entity_id && (
                <p className="text-xs text-red-500">Please enter Advertiser ID to auto-fill</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Bill/Receipt in Name of</Label>
              <Input value={formData.bill_receipt_name} onChange={(e) => setFormData({ ...formData, bill_receipt_name: e.target.value })} placeholder="Enter name" />
            </div>

            <div className="space-y-2">
              <Label>Billing Address</Label>
              <Textarea 
                value={formData.billing_address} 
                onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })} 
                placeholder={formData.type === 'Advertiser' ? 'Auto-filled from advertiser (editable)' : 'Enter billing address'}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>GST Number (Optional)</Label>
              <Input value={formData.gst_no} onChange={(e) => setFormData({ ...formData, gst_no: e.target.value })} placeholder="Enter GST number" />
            </div>

            <div className="space-y-2">
              <Label>Billing State *</Label>
              <Select 
                value={formData.billing_state} 
                onValueChange={(value) => {
                  setFormData({ ...formData, billing_state: value, district: '' });
                  fetchDistricts(value);
                }}
              >
                <SelectTrigger data-testid="billing-state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Andhra Pradesh">Andhra Pradesh</SelectItem>
                  <SelectItem value="Arunachal Pradesh">Arunachal Pradesh</SelectItem>
                  <SelectItem value="Assam">Assam</SelectItem>
                  <SelectItem value="Bihar">Bihar</SelectItem>
                  <SelectItem value="Chhattisgarh">Chhattisgarh</SelectItem>
                  <SelectItem value="Delhi">Delhi</SelectItem>
                  <SelectItem value="Goa">Goa</SelectItem>
                  <SelectItem value="Gujarat">Gujarat</SelectItem>
                  <SelectItem value="Haryana">Haryana</SelectItem>
                  <SelectItem value="Himachal Pradesh">Himachal Pradesh</SelectItem>
                  <SelectItem value="Jharkhand">Jharkhand</SelectItem>
                  <SelectItem value="Karnataka">Karnataka</SelectItem>
                  <SelectItem value="Kerala">Kerala</SelectItem>
                  <SelectItem value="Madhya Pradesh">Madhya Pradesh</SelectItem>
                  <SelectItem value="Maharashtra">Maharashtra</SelectItem>
                  <SelectItem value="Manipur">Manipur</SelectItem>
                  <SelectItem value="Meghalaya">Meghalaya</SelectItem>
                  <SelectItem value="Mizoram">Mizoram</SelectItem>
                  <SelectItem value="Nagaland">Nagaland</SelectItem>
                  <SelectItem value="Odisha">Odisha</SelectItem>
                  <SelectItem value="Punjab">Punjab</SelectItem>
                  <SelectItem value="Rajasthan">Rajasthan</SelectItem>
                  <SelectItem value="Sikkim">Sikkim</SelectItem>
                  <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
                  <SelectItem value="Telangana">Telangana</SelectItem>
                  <SelectItem value="Tripura">Tripura</SelectItem>
                  <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
                  <SelectItem value="Uttarakhand">Uttarakhand</SelectItem>
                  <SelectItem value="West Bengal">West Bengal</SelectItem>
                  <SelectItem value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</SelectItem>
                  <SelectItem value="Chandigarh">Chandigarh</SelectItem>
                  <SelectItem value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</SelectItem>
                  <SelectItem value="Jammu and Kashmir">Jammu and Kashmir</SelectItem>
                  <SelectItem value="Ladakh">Ladakh</SelectItem>
                  <SelectItem value="Lakshadweep">Lakshadweep</SelectItem>
                  <SelectItem value="Puducherry">Puducherry</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Required for GST calculation (Aabkari Times only)</p>
            </div>

            <div className="space-y-2">
              <Label>District</Label>
              <Input 
                list="districts-list"
                value={formData.district} 
                onChange={(e) => setFormData({ ...formData, district: e.target.value })} 
                placeholder={formData.type === 'Advertiser' ? 'Auto-filled from advertiser (editable)' : 'Select or type district'}
              />
              <datalist id="districts-list">
                {districts.map((district) => (
                  <option key={district} value={district} />
                ))}
              </datalist>
            </div>

            <div className="space-y-2">
              <Label>Pincode</Label>
              <Input 
                value={formData.pincode} 
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} 
                placeholder={formData.type === 'Advertiser' ? 'Auto-filled from advertiser (editable)' : 'Enter pincode'}
              />
            </div>

            <div className="space-y-2">
              <Label>Occasion</Label>
              <Select value={formData.occasion || 'NONE'} onValueChange={(value) => setFormData({ ...formData, occasion: value === 'NONE' ? '' : value })}>
                <SelectTrigger data-testid="occasion-select">
                  <SelectValue placeholder="Select occasion (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  <SelectItem value="Special Issue">Special Issue</SelectItem>
                  <SelectItem value="15th August Special Issue">15th August Special Issue</SelectItem>
                  <SelectItem value="26th January Special Issue">26th January Special Issue</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Month and Year */}
            <div className="space-y-2">
              <Label>Start Month</Label>
              <Select value={formData.start_month} onValueChange={(value) => setFormData({ ...formData, start_month: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="January">January</SelectItem>
                  <SelectItem value="February">February</SelectItem>
                  <SelectItem value="March">March</SelectItem>
                  <SelectItem value="April">April</SelectItem>
                  <SelectItem value="May">May</SelectItem>
                  <SelectItem value="June">June</SelectItem>
                  <SelectItem value="July">July</SelectItem>
                  <SelectItem value="August">August</SelectItem>
                  <SelectItem value="September">September</SelectItem>
                  <SelectItem value="October">October</SelectItem>
                  <SelectItem value="November">November</SelectItem>
                  <SelectItem value="December">December</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Year</Label>
              <Select value={formData.start_year} onValueChange={(value) => setFormData({ ...formData, start_year: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {Array.from({ length: 25 }, (_, i) => (2021 + i).toString()).map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* End Month and Year */}
            <div className="space-y-2">
              <Label>End Month</Label>
              <Select value={formData.end_month} onValueChange={(value) => setFormData({ ...formData, end_month: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="January">January</SelectItem>
                  <SelectItem value="February">February</SelectItem>
                  <SelectItem value="March">March</SelectItem>
                  <SelectItem value="April">April</SelectItem>
                  <SelectItem value="May">May</SelectItem>
                  <SelectItem value="June">June</SelectItem>
                  <SelectItem value="July">July</SelectItem>
                  <SelectItem value="August">August</SelectItem>
                  <SelectItem value="September">September</SelectItem>
                  <SelectItem value="October">October</SelectItem>
                  <SelectItem value="November">November</SelectItem>
                  <SelectItem value="December">December</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>End Year</Label>
              <Select value={formData.end_year} onValueChange={(value) => setFormData({ ...formData, end_year: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {Array.from({ length: 25 }, (_, i) => (2021 + i).toString()).map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Advertisement fields - Only show for Advertiser type */}
            {formData.type === 'Advertiser' && (
              <>
                <div className="space-y-2">
                  <Label>Advertisement Position</Label>
                  <Select value={formData.advertisement_position} onValueChange={(value) => setFormData({ ...formData, advertisement_position: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Front Cover Inside">Front Cover Inside</SelectItem>
                      <SelectItem value="Full Page Inside">Full Page Inside</SelectItem>
                      <SelectItem value="Full Page">Full Page</SelectItem>
                      <SelectItem value="Back Cover Inside">Back Cover Inside</SelectItem>
                      <SelectItem value="Back Cover">Back Cover</SelectItem>
                      <SelectItem value="Half Page">Half Page</SelectItem>
                      <SelectItem value="Bottom Strip">Bottom Strip</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Advertisement Tenure</Label>
                  <Select value={formData.advertisement_tenure} onValueChange={(value) => setFormData({ ...formData, advertisement_tenure: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select tenure" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 Month">1 Month</SelectItem>
                      <SelectItem value="2 Months">2 Months</SelectItem>
                      <SelectItem value="3 Months">3 Months</SelectItem>
                      <SelectItem value="4 Months">4 Months</SelectItem>
                      <SelectItem value="5 Months">5 Months</SelectItem>
                      <SelectItem value="6 Months">6 Months</SelectItem>
                      <SelectItem value="1 Year">1 Year</SelectItem>
                      <SelectItem value="Alternate Months">Alternate Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Period/Duration - Different options based on type */}
            <div className="space-y-2">
              <Label>Period/Duration</Label>
              <Select value={formData.advertisement_tenure} onValueChange={(value) => setFormData({ ...formData, advertisement_tenure: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period/duration" />
                </SelectTrigger>
                <SelectContent>
                  {formData.type === 'Member' ? (
                    <>
                      <SelectItem value="1 Year">1 Year</SelectItem>
                      <SelectItem value="2 Years">2 Years</SelectItem>
                      <SelectItem value="3 Years">3 Years</SelectItem>
                      <SelectItem value="Lifetime">Lifetime</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="1 Month">1 Month</SelectItem>
                      <SelectItem value="2 Months">2 Months</SelectItem>
                      <SelectItem value="3 Months">3 Months</SelectItem>
                      <SelectItem value="4 Months">4 Months</SelectItem>
                      <SelectItem value="5 Months">5 Months</SelectItem>
                      <SelectItem value="6 Months">6 Months</SelectItem>
                      <SelectItem value="1 Year">1 Year</SelectItem>
                      <SelectItem value="Alternate Months">Alternate Months</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Item Description */}
            <div className="space-y-2 md:col-span-2">
              <Label>Item Description</Label>
              <Textarea
                value={formData.item_description}
                onChange={(e) => {
                  setUserEditedDescription(true);
                  setFormData({ ...formData, item_description: e.target.value });
                }}
                placeholder="Enter item description..."
                rows={2}
              />
              <p className="text-xs text-gray-500">
                {userEditedDescription
                  ? 'Custom description — will not be auto-overwritten. '
                  : 'Default auto-fills until you edit it. '}
                <button
                  type="button"
                  data-testid="reset-description-btn"
                  className="text-blue-600 underline ml-1 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    const product = formData.product || 'Subscription';
                    const mag = formData.magazine ? formData.magazine + ' magazine' : 'Magazine';
                    const tenure = formData.advertisement_tenure || '1 Month';
                    const defaultDesc = `Towards your ${product} charges of ${mag} for ${tenure}`;
                    setFormData(prev => ({ ...prev, item_description: defaultDesc }));
                    setUserEditedDescription(false);
                  }}
                >Reset to default</button>
              </p>
            </div>

            {/* Include in Aabkari checkbox - Only for Sugar Times Advertisers */}
            {formData.magazine === 'Sugar Times' && formData.type === 'Advertiser' && (
              <div className="space-y-2 md:col-span-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="include_in_aabkari"
                    checked={formData.include_in_aabkari}
                    onChange={(e) => setFormData({ ...formData, include_in_aabkari: e.target.checked })}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <Label htmlFor="include_in_aabkari" className="cursor-pointer font-semibold">
                    Include advertisement in Aabkari Times also (Complimentary Offer)
                  </Label>
                </div>
                <p className="text-xs text-gray-500 ml-6">This will mark the advertiser as having cross-magazine offer</p>
              </div>
            )}

            {/* GST Rate Override + HSN/SAC */}
            <div className="space-y-2">
              <Label>GST Rate</Label>
              <Select
                value={formData.gst_rate || 'auto'}
                onValueChange={(value) => setFormData({ ...formData, gst_rate: value, last_edited_field: formData.last_edited_field || 'gross' })}
              >
                <SelectTrigger data-testid="gst-rate-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (by product)</SelectItem>
                  <SelectItem value="0">0% (No GST)</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="18">18%</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Effective rate: {(getEffectiveGstRate(formData) * 100).toFixed(0)}%
              </p>
            </div>

            <div className="space-y-2">
              <Label>HSN / SAC Code</Label>
              <Input
                value={formData.hsn_sac || ''}
                onChange={(e) => setFormData({ ...formData, hsn_sac: e.target.value })}
                placeholder="998363 / 998397"
                data-testid="hsn-sac-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Gross Amount *</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={formData.gross_amount} 
                onChange={(e) => setFormData({ ...formData, gross_amount: e.target.value, last_edited_field: 'gross' })} 
                placeholder="Enter gross amount"
                data-testid="gross-amount-input"
              />
              <p className="text-xs text-gray-500">Gross − Discount = Taxable; GST added on top</p>
            </div>

            <div className="space-y-2">
              <Label>Discount % (Optional)</Label>
              <Input 
                type="number" 
                step="0.01"
                value={formData.discount_percentage} 
                onChange={(e) => {
                  const pct = e.target.value;
                  const gross = parseFloat(formData.gross_amount) || 0;
                  const discAmt = pct && gross ? Math.round(gross * parseFloat(pct) / 100 * 100) / 100 : '';
                  setFormData({ ...formData, discount_percentage: pct, discount_amount: discAmt.toString(), last_edited_field: 'discount' });
                }}
                placeholder="e.g. 20" 
              />
            </div>

            <div className="space-y-2">
              <Label>Discount Amount</Label>
              <Input 
                type="number" 
                step="0.01"
                value={formData.discount_amount} 
                onChange={(e) => {
                  const discAmt = e.target.value;
                  const gross = parseFloat(formData.gross_amount) || 0;
                  const pct = discAmt && gross ? Math.round(parseFloat(discAmt) / gross * 100 * 100) / 100 : '';
                  setFormData({ ...formData, discount_amount: discAmt, discount_percentage: pct.toString(), last_edited_field: 'discount' });
                }}
                placeholder="Auto-calculated or enter manually"
                className="bg-gray-50"
              />
            </div>

            {/* GST Breakdown (visible whenever effective rate > 0) */}
            {getEffectiveGstRate(formData) > 0 && (
              <>
                <div className="space-y-2">
                  <Label>SGST</Label>
                  <Input type="number" value={calculatedFields.sgst_amount.toFixed(2)} disabled className="bg-gray-100" />
                </div>

                <div className="space-y-2">
                  <Label>CGST</Label>
                  <Input type="number" value={calculatedFields.cgst_amount.toFixed(2)} disabled className="bg-gray-100" />
                </div>

                <div className="space-y-2">
                  <Label>IGST</Label>
                  <Input type="number" value={calculatedFields.igst_amount.toFixed(2)} disabled className="bg-gray-100" />
                </div>
              </>
            )}

            <div className="space-y-2 md:col-span-2">
              <Label className="text-lg font-bold text-green-700">Grand Total (Inclusive of GST)</Label>
              <Input 
                type="number"
                step="0.01"
                value={
                  formData.last_edited_field === 'total'
                    ? (formData.total_amount ?? '')
                    : (calculatedFields.total_amount ? calculatedFields.total_amount.toFixed(2) : '')
                }
                onChange={(e) => setFormData({ ...formData, total_amount: e.target.value, last_edited_field: 'total' })}
                placeholder="Type inclusive total to reverse-calc Gross & GST"
                className="bg-green-50 font-bold text-xl text-green-800 border-green-300"
                data-testid="total-amount-input"
              />
              <p className="text-xs text-gray-500">Type here for GST-inclusive: Taxable = Total / (1 + Rate). Gross will reverse-calculate.</p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Amount in Words</Label>
              <Input 
                value={calculatedFields.amount_in_words} 
                disabled 
                className="bg-gray-100 font-semibold" 
              />
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <Label>Terms & Conditions</Label>
            <Textarea value={formData.terms_conditions} onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })} rows={3} />
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#7c3aed' }} data-testid="submit-button">
              <Save className="mr-2" size={18} />
              {loading ? 'Creating...' : 'REGISTER'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnterBill;